import { useEffect, useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { useGlobalWorldStatus } from "@/hooks/useGlobalWorldStatus";
import { useLanguage } from "@/hooks/useLanguage";
import { formatStatus } from "@/lib/global/globalMatchEventsEngine";
import { useMobileMatchMode } from "@/lib/launchHardening/mobileMatchMode";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { shareNation } from "@/lib/whatsappViral/whatsappEngine";

/**
 * One-line cinematic world status. Updates softly with cached signals.
 * Includes a share affordance for viral global moments.
 */
export function WorldStatusBanner({ matchMinute }: { matchMinute?: number }) {
  const { t, lang } = useLanguage();
  const s = useGlobalWorldStatus({ matchMinute });
  const mmm = useMobileMatchMode();
  // P0-SHARE-02 — use the user's selected nation, not geo IP.
  const { nation: nationCode } = useSelectedNation();
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    setPulse(true);
    const id = window.setTimeout(() => setPulse(false), 700);
    return () => window.clearTimeout(id);
  }, [s.statusKey]);

  const text = formatStatus(t(s.statusKey), s.statusTokens);

  const onShare = async () => {
    const res = await shareNation(
      { nationCode: nationCode ?? "" },
      { source: "world_status_banner", lang },
    );
    if (!res.ok) toast.error(t("cm_copy_failed"));
  };

  const onWhatsApp = () => {
    void shareNation(
      { nationCode: nationCode ?? "" },
      { source: "world_status_banner", lang, preferNative: false },
    );
  };

  const glow = (8 + s.energy * 28) * mmm.glowScale;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative overflow-hidden rounded-xl border border-border/60 bg-background/80 px-3 py-2 backdrop-blur transition-transform duration-300 ${pulse ? "scale-[1.01]" : "scale-100"}`}
      style={{ boxShadow: `0 0 ${glow}px oklch(0.7 0.22 30 / ${0.18 + s.energy * 0.32})` }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(60% 80% at 0% 50%, oklch(0.78 0.2 ${60 - s.energy * 38} / 0.18) 0%, transparent 70%)`,
        }}
      />
      <div className="relative flex items-center gap-2">
        <div className="min-w-0 flex-1 truncate text-sm font-black">{text}</div>
        <button
          type="button"
          onClick={onShare}
          className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground active:scale-95"
          aria-label={t("cm_share")}
        >
          <Share2 className="h-3 w-3" /> {t("cm_share")}
        </button>
        <button
          type="button"
          onClick={onWhatsApp}
          className="hidden sm:inline rounded-full border border-border bg-[#25D366] px-2.5 py-1 text-[11px] font-bold text-white active:scale-95"
          aria-label="WhatsApp"
        >
          WhatsApp
        </button>
      </div>
    </div>
  );
}